import React, { useState, useRef, useEffect } from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import * as Tabs from '@radix-ui/react-tabs';
import {
  X, Upload, Trash2, Edit, Save,
  User, FileText, Download, File,
  Plus, CheckCircle, BookOpen, Accessibility,
  Paperclip, XCircle, Fingerprint, AlertTriangle,
  Clock, Calendar, ChevronDown, History,
  Briefcase, DollarSign, Check
} from 'lucide-react';
import api from '../services/api';
import { format, parseISO } from 'date-fns';
import { useFeatureFlag } from '../hooks/useFeatureFlag';
import { useParamsCompta } from '../contexts/ParametresComptaContext';

// ── Utilitaires ────────────────────────────────────────────────────────────
const fmt = (d) => {
  if (!d) return 'Non renseigné';
  try { return format(parseISO(d), 'dd/MM/yyyy'); } catch { return 'Date invalide'; }
};

const fmtInput = (d) => {
  if (!d) return '';
  try {
    if (/^\d{4}-\d{2}-\d{2}$/.test(d)) return d;
    const date = new Date(d);
    if (isNaN(date.getTime())) return '';
    return `${date.getFullYear()}-${String(date.getMonth()+1).padStart(2,'0')}-${String(date.getDate()).padStart(2,'0')}`;
  } catch { return ''; }
};

const fmtCurrency = (n) => {
  if (!n) return '0 FCFA';
  return new Intl.NumberFormat('fr-FR', { style: 'decimal', minimumFractionDigits: 0 }).format(n) + ' FCFA';
};

// ── Input réutilisable ─────────────────────────────────────────────────────
const Field = ({ label, children, required, hint }) => (
  <div>
    <label className="block text-xs font-semibold text-gray-600 mb-1.5">
      {label}
      {required && <span className="text-red-500 ml-1">*</span>}
      {hint && <span className="text-amber-500 ml-2 font-normal text-xs">{hint}</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full px-3 py-2.5 border border-gray-200 rounded-lg font-medium text-sm focus:outline-none transition-all";
const inputEvents = {
  onFocus: (e) => { e.target.style.borderColor = '#448696'; e.target.style.boxShadow = '0 0 0 3px rgba(68,134,150,0.1)'; },
  onBlur:  (e) => { e.target.style.borderColor = '#e5e7eb'; e.target.style.boxShadow = 'none'; }
};
const ReadVal = ({ children, colored }) => (
  <div className={`bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100 font-medium text-sm ${colored ? '' : 'text-gray-900'}`}
    style={colored ? { color: '#448696' } : {}}>
    {children}
  </div>
);

// ── Étiquette statut événement ─────────────────────────────────────────────
const EventBadge = ({ type }) => {
  const cfg = {
    prolongation_essai:  { label: 'Prolongation essai',  color: '#b08d1a', bg: '#fef9c3' },
    confirmation_contrat:{ label: 'Contrat confirmé',    color: '#16a34a', bg: '#dcfce7' },
    fin_contrat:         { label: 'Fin de contrat',      color: '#dc2626', bg: '#fee2e2' },
  };
  const c = cfg[type] || { label: type, color: '#448696', bg: '#f0f9fb' };
  return (
    <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold"
      style={{ backgroundColor: c.bg, color: c.color }}>
      {c.label}
    </span>
  );
};

// ── Section document réutilisable ──────────────────────────────────────────
const DocSection = ({ title, icon: Icon, type, docs, onUpload, onDownload, onDelete, multiple = false, accept = "application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" }) => {
  const filtered = type === 'other'
    ? docs.filter(d => !['contrat','cv','piece_identite','diplome'].includes(d.type))
    : docs.filter(d => d.type === type);

  const triggerUpload = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = accept;
    if (multiple) input.multiple = true;
    input.onchange = (e) => onUpload(e, type);
    input.click();
  };

  return (
    <div className="bg-white rounded-xl overflow-hidden border border-gray-100 shadow-sm">
      <div className="px-5 py-3.5 flex items-center justify-between"
        style={{ background: 'linear-gradient(135deg, #448696, #6fa3b0)' }}>
        <div className="flex items-center gap-2 text-white font-bold text-sm">
          <Icon size={17} /> {title}
        </div>
        <button onClick={triggerUpload}
          className="flex items-center gap-1.5 px-3 py-1.5 bg-white/20 hover:bg-white/30 rounded-lg text-white text-xs font-semibold transition-all">
          <Plus size={14} /> Ajouter
        </button>
      </div>
      <div className="p-5">
        {filtered.length === 0 ? (
          <div className="border-2 border-dashed border-gray-200 rounded-xl py-8 flex flex-col items-center gap-3">
            <div className="w-12 h-12 bg-gray-100 rounded-full flex items-center justify-center">
              <Icon size={22} className="text-gray-300" />
            </div>
            <p className="text-sm text-gray-400">Aucun document</p>
            <button onClick={triggerUpload}
              className="text-xs font-semibold px-4 py-2 rounded-lg text-white transition-all hover:opacity-90"
              style={{ backgroundColor: '#448696' }}>
              Ajouter
            </button>
          </div>
        ) : (
          <div className="space-y-2">
            {filtered.map((doc, i) => (
              <div key={i} className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-100 hover:shadow-sm transition-all">
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0">
                    <FileText size={17} className="text-blue-600" />
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-gray-800">{doc.nom_fichier || `Document ${i+1}`}</p>
                    <p className="text-xs text-gray-400">{doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString('fr-FR') : '—'}</p>
                  </div>
                </div>
                <div className="flex gap-1.5">
                  <button onClick={() => onDownload(doc.id, doc.nom_fichier)}
                    className="w-8 h-8 rounded-lg hover:bg-blue-50 flex items-center justify-center transition-all">
                    <Download size={15} className="text-blue-600" />
                  </button>
                  <button onClick={() => onDelete(doc.id, doc.type)}
                    className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center transition-all">
                    <Trash2 size={15} className="text-red-500" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// ── Composant principal ────────────────────────────────────────────────────
const EmployeeDetailModal = ({ employee, isOpen, onClose, onUpdate, onShowToast }) => {
  const [isEditing, setIsEditing]         = useState(false);
  const [localEmp, setLocalEmp]           = useState({ ...employee });
  const [photoPreview, setPhotoPreview]   = useState(null);
  const [isUploading, setIsUploading]     = useState(false);
  const [error, setError]                 = useState(null);
  const [activeTab, setActiveTab]         = useState('identity');
  const [documents, setDocuments]         = useState([]);
  const [contractTypes, setContractTypes] = useState([]);

  const [departments, setDepartments] = useState([]);
  const [deptDropdownOpen, setDeptDropdownOpen] = useState(false);
  const deptDropdownRef = useRef(null);

  const [contractEvents, setContractEvents] = useState([]);
  const [modeDemarrage, setModeDemarrage] = useState('manuel');
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);

  // États actions contrat
  const [contractAction, setContractAction]   = useState(null); // 'prolonger' | 'confirmer' | 'fin'
  const [actionData, setActionData]           = useState({});
  const [savingAction, setSavingAction]       = useState(false);

  // Dropdown type contrat
  const [contractTypeOpen, setContractTypeOpen] = useState(false);
  const contractTypeRef = useRef(null);
  const fileInputRef    = useRef(null);

  const [groupesSalariaux, setGroupesSalariaux] = useState([]);
  const [groupeDropdownOpen, setGroupeDropdownOpen] = useState(false);
  const groupeDropdownRef = useRef(null);
  const { symbole } = useParamsCompta();


  // FEATURS FLAGS
  const [rfidCapture, setRfidCapture]       = useState('');
  const [rfidFieldActive, setRfidFieldActive] = useState(false);
  const { isEnabled: rfidEnabled } = useFeatureFlag('rfid_attendance');
  const { isEnabled: contratsEnabled } = useFeatureFlag('contrats_employes');

  // ── Init ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!employee || !isOpen) return;

    setLocalEmp({
      ...employee,
      hire_date:            fmtInput(employee.hire_date),
      date_of_birth:        fmtInput(employee.date_of_birth),
      contract_start_date:  fmtInput(employee.contract_start_date),
      contract_end_date:    fmtInput(employee.contract_end_date),
      trial_period_end_date:fmtInput(employee.trial_period_end_date),
    });

    if (employee.photo_url) {
      setPhotoPreview(`${process.env.REACT_APP_API_URL?.replace('/api','')}${employee.photo_url}?t=${Date.now()}`);
    } else {
      setPhotoPreview(null);
    }

    // Charger en parallèle
   const load = async () => {
    try {
      const apiCalls = [api.departements.getAll()];
      
      if (contratsEnabled) {
        apiCalls.push(api.contractTypes.getAll());
        apiCalls.push(api.parametreContrat.get());
      }
      
      const results = await Promise.all(apiCalls);
      const deptRes = results[0];
      const ctRes = contratsEnabled ? results[1] : null;
      const contratRes = contratsEnabled ? results[2] : null;
      
      if (contratsEnabled && ctRes?.success) setContractTypes(ctRes.data);
      if (contratsEnabled && contratRes?.success) setModeDemarrage(contratRes.data.mode_demarrage_contrat || 'manuel');
      const deptData = deptRes.data || deptRes;
      setDepartments(Array.isArray(deptData) ? deptData : []);
    } catch (e) {
      console.error('Erreur chargement types/contrat:', e);
    }


      try {
        const evRes = await api.contractEvents.getAll(employee.id);
        if (evRes.success) setContractEvents(evRes.data);
      } catch (e) {
        console.error('Erreur chargement events:', e);
        setContractEvents([]);
      }

      try {
        const gRes = await api.groupesSalariaux.getAll();
        if (gRes.success) setGroupesSalariaux(gRes.data);
      } catch (e) { console.error('Erreur chargement groupes:', e); }

      loadDocuments();
    };
    load();

  }, [employee, isOpen]);


useEffect(() => {
    const h = (e) => {
      if (contractTypeRef.current && !contractTypeRef.current.contains(e.target)) setContractTypeOpen(false);
      if (deptDropdownRef.current && !deptDropdownRef.current.contains(e.target)) setDeptDropdownOpen(false);
      if (groupeDropdownRef.current && !groupeDropdownRef.current.contains(e.target)) setGroupeDropdownOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);


  const loadDocuments = async () => {
    if (!employee?.id) return;
    try {
      const res = await api.employees.getEmployeeDocuments(employee.id);
      if (res?.success) setDocuments(res.documents || []);
      else setDocuments([]);
    } catch { setDocuments([]); }
  };

  // ── Toast helpers ────────────────────────────────────────────────────────
  const ok  = (m) => onShowToast?.({ message: m, type: 'success' });
  const err = (m) => onShowToast?.({ message: m, type: 'error' });
  const inf = (m) => onShowToast?.({ message: m, type: 'loading' });

  // ── Handlers ─────────────────────────────────────────────────────────────
  const handleInput = (e) => {
    const { name, value } = e.target;
    setLocalEmp(prev => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
    try {
      inf('Enregistrement...');
      const response = await api.employees.update(employee.id, {
        first_name:           localEmp.first_name,
        last_name:            localEmp.last_name,
        email:                localEmp.email,
        phone:                localEmp.phone,
        date_of_birth:        localEmp.date_of_birth,
        gender:               localEmp.gender,
        address:              localEmp.address,
        city:                 localEmp.city,
        position:             localEmp.position,
        department:           localEmp.department,
        hire_date:            localEmp.hire_date,
        salary:               localEmp.salary,
        salary_type:          localEmp.salary_type,
        badge_number:         localEmp.badge_number,
        rfid_uid:             localEmp.rfid_uid,
        status:               localEmp.status,
        contract_type_id:     localEmp.contract_type_id || null,
        contract_start_date:  localEmp.contract_start_date || null,
        contract_end_date:    localEmp.contract_end_date || null,
        trial_period_end_date:localEmp.trial_period_end_date || null,
        mode_paiement_salaire: localEmp.mode_paiement_salaire || 'especes',
        groupe_salarial_id: localEmp.groupe_salarial_id || null,
        taux_horaire_personnalise: localEmp.taux_horaire_personnalise || null,
      });
      if (response?.success) {
        const updated = {
          ...response,
          hire_date:            fmtInput(response.hire_date),
          date_of_birth:        fmtInput(response.date_of_birth),
          contract_start_date:  fmtInput(response.contract_start_date),
          contract_end_date:    fmtInput(response.contract_end_date),
          trial_period_end_date:fmtInput(response.trial_period_end_date),
        };
        setLocalEmp(updated);
        setIsEditing(false);
        ok('Modifications enregistrées');
        onUpdate?.(updated);
      }
    } catch (e) { err('Erreur lors de l\'enregistrement'); }
  };

  const handleCancel = () => { setLocalEmp({ ...employee }); setIsEditing(false); setError(null); };

  // Photo
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    if (!['image/jpeg','image/png','image/jpg'].includes(file.type)) { err('Format non supporté'); return; }
    if (file.size > 5*1024*1024) { err('Image trop lourde (max 5MB)'); return; }
    const reader = new FileReader();
    reader.onload = (ev) => setPhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
    setIsUploading(true);
    inf('Upload photo...');
    try {
      const fd = new FormData(); fd.append('photo', file);
      const res = await api.employees.uploadPhoto(employee.id, fd);
      if (res.photo_url) {
        const upd = { ...localEmp, photo_url: res.photo_url };
        setLocalEmp(upd); onUpdate?.(upd);
        setPhotoPreview(`${process.env.REACT_APP_API_URL?.replace('/api','')}${res.photo_url}?t=${Date.now()}`);
        ok('Photo ajoutée');
      }
    } catch { err('Erreur upload photo'); } finally { setIsUploading(false); }
  };

  const handleDeletePhoto = async () => {
    inf('Suppression photo...'); 
    try {
      await api.employees.deletePhoto(employee.id);
      setPhotoPreview(null);
      const upd = { ...localEmp, photo_url: null };
      setLocalEmp(upd); onUpdate?.(upd); ok('Photo supprimée');
    } catch { err('Erreur suppression photo'); }
  };

  // Documents
  const handleFileUpload  = async (e, type) => {
    const file = e.target.files[0]; if (!file) return;
    const allowed = ['application/pdf','application/msword','application/vnd.openxmlformats-officedocument.wordprocessingml.document','image/jpeg','image/png','image/jpg'];
    if (!allowed.includes(file.type)) { err('Format non supporté'); return; }
    if (file.size > 10*1024*1024) { err('Fichier trop lourd (max 10MB)'); return; }
    inf('Ajout document...');
    try {
      const fd = new FormData(); fd.append('document', file);
      const res = await api.employees.uploadDocument(employee.id, fd, type);
      if (res.success) { ok('Document ajouté'); await loadDocuments(); }
      else err(res.message || 'Erreur upload');
    } catch { err('Erreur upload document'); }
  };
  const handleDownload = async (docId, docName) => {
    try { await api.employees.downloadDocument(employee.id, docId); ok('Téléchargement lancé'); }
    catch { err('Erreur téléchargement'); }
  };
  const handleDeleteDoc = async (docId, type) => {
    inf('Suppression...');
    try {
      const res = await api.employees.deleteDocument(employee.id, docId);
      if (res.success) { ok('Document supprimé'); await loadDocuments(); }
      else err('Erreur suppression');
    } catch { err('Erreur suppression document'); }
  };

  // Génération contrat PDF
  const generateContract = async () => {
    setIsGeneratingContract(true);
    try {
      const res = await api.employees.generateEmployeeContract({ employee: { id: localEmp.id, first_name: localEmp.first_name, last_name: localEmp.last_name, email: localEmp.email, hire_date: localEmp.hire_date, date_of_birth: localEmp.date_of_birth, address: localEmp.address, department: localEmp.department, position: localEmp.position } });
      if (res.success) { ok('Contrat généré'); await loadDocuments(); }
      else err('Erreur génération contrat');
    } catch { err('Erreur génération contrat'); } finally { setIsGeneratingContract(false); }
  };

  // RFID
  const handleRfidKeyDown = (e) => {
    if ([16,17,18].includes(e.keyCode)) return;
    if (e.keyCode === 13 && rfidCapture.trim()) {
      e.preventDefault();
      setLocalEmp(prev => ({ ...prev, rfid_uid: rfidCapture.trim() }));
      setRfidCapture(''); ok(`Badge RFID capturé : ${rfidCapture.trim()}`); return;
    }
    if (!((e.keyCode >= 48 && e.keyCode <= 57) || (e.keyCode >= 65 && e.keyCode <= 90))) { e.preventDefault(); return; }
    setRfidCapture(prev => prev + String.fromCharCode(e.keyCode));
    clearTimeout(window.rfidDetailTimeout);
    window.rfidDetailTimeout = setTimeout(() => setRfidCapture(''), 500);
    e.preventDefault();
  };

  // ── Action contrat ────────────────────────────────────────────────────────
  const handleContractAction = async () => {
    if (!contractAction) return;
    setSavingAction(true);
    try {
      const res = await api.contractEvents.action(employee.id, { action: contractAction, ...actionData });
      if (res.success) {
        ok('Action effectuée avec succès');
        setContractAction(null);
        setActionData({});
        // Recharger les events
        const evRes = await api.contractEvents.getAll(employee.id);
        if (evRes.success) setContractEvents(evRes.data);
        // Mettre à jour l'employé
        const updated = { ...res.data, hire_date: fmtInput(res.data.hire_date), date_of_birth: fmtInput(res.data.date_of_birth), contract_start_date: fmtInput(res.data.contract_start_date), contract_end_date: fmtInput(res.data.contract_end_date), trial_period_end_date: fmtInput(res.data.trial_period_end_date) };
        setLocalEmp(updated);
        onUpdate?.(updated);
      } else err(res.message || 'Erreur');
    } catch { err('Erreur lors de l\'action'); } finally { setSavingAction(false); }
  };

  // ── Calcul alerte période d'essai ─────────────────────────────────────────
  const today = new Date(); today.setHours(0,0,0,0);
  const isTrialEnded = localEmp.trial_period_end_date && new Date(localEmp.trial_period_end_date) < today;
  const showTrialAlert = isTrialEnded && modeDemarrage === 'manuel';

  const hasExistingContract = () => documents.filter(d => d.type === 'contrat').length > 0;

  // ── Onglet actif selon alerte ─────────────────────────────────────────────
  const tabClass = (t) => `px-4 py-3 font-semibold text-sm flex items-center gap-2 transition-colors border-b-2 ${
    activeTab === t ? 'text-[#448696] border-[#448696]' : 'text-gray-500 border-transparent hover:text-gray-700'
  }`;

  // ── RENDU ─────────────────────────────────────────────────────────────────
  return (
    <Dialog.Root open={isOpen} onOpenChange={() => {}}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/60" style={{ zIndex: 9998 }} />
        <Dialog.Content
          className="fixed top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-full max-w-5xl max-h-[90vh] rounded-2xl shadow-2xl flex flex-col"
          style={{ zIndex: 9999, backgroundColor: '#fdfcf5', border: '1px solid #e5e7eb' }}>

          <Tabs.Root value={activeTab} onValueChange={setActiveTab} className="flex flex-col flex-1 min-h-0">

            {/* ── HEADER ── */}
            <div className="flex-shrink-0 rounded-t-2xl overflow-hidden">
              <div className="px-6 py-4 flex items-center justify-between"
                style={{ background: 'linear-gradient(135deg, #2d6a7f, #448696)' }}>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center">
                    <User size={20} className="text-white" />
                  </div>
                  <div>
                    <Dialog.Title className="text-base font-bold text-white">
                      {localEmp.first_name} {localEmp.last_name}
                    </Dialog.Title>
                    <p className="text-xs text-white/70 mt-0.5">
                      {localEmp.position || 'Poste non défini'} {localEmp.department ? `· ${localEmp.department}` : ''}
                    </p>
                  </div>
                  {showTrialAlert && (
                    <div className="ml-4 flex items-center gap-2 px-3 py-1.5 rounded-xl text-xs font-bold"
                      style={{ backgroundColor: '#ffedd5', color: '#ea580c' }}>
                      <AlertTriangle size={13} />
                      Période d'essai terminée - Action requise
                    </div>
                  )}
                </div>
                <button onClick={onClose} className="w-8 h-8 rounded-lg bg-white/15 hover:bg-white/25 flex items-center justify-center text-white transition-all">
                  <X size={17} />
                </button>
              </div>

           
              {/* Onglets */}

          <Tabs.List className="flex bg-white border-b border-gray-100 px-2">
          <Tabs.Trigger value="identity"  className={tabClass('identity')}>
            <User size={16} /> Identité
          </Tabs.Trigger>
          {contratsEnabled && (
            <Tabs.Trigger value="contract"  className={tabClass('contract')}>
              <Briefcase size={16} />
              Poste & Contrat
              {showTrialAlert && <span className="w-2 h-2 rounded-full bg-orange-500 animate-pulse ml-1" />}
            </Tabs.Trigger>
          )}
          <Tabs.Trigger value="documents" className={tabClass('documents')}>
            <Paperclip size={16} /> Documents
          </Tabs.Trigger>
        </Tabs.List>

            </div>

            {/* Erreur */}
            {error && (
              <div className="mx-6 mt-4 p-3 rounded-xl bg-red-50 border border-red-200 flex items-center gap-2 text-sm text-red-700 font-semibold">
                <AlertTriangle size={16} /> {error}
              </div>
            )}

            {/* ── CONTENU SCROLLABLE ── */}
            <div className="flex-1 overflow-y-auto min-h-0">

              {/* ═══════════════════════════════════════════════════
                  ONGLET 1 — IDENTITÉ
              ═══════════════════════════════════════════════════ */}
              <Tabs.Content value="identity">
                <div className="p-6 grid grid-cols-1 md:grid-cols-3 gap-5 pb-10">

                  {/* Col 1 — Photo */}
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Photo</p>
                      <div className="flex flex-col items-center">
                        <div className="relative w-36 h-36 rounded-2xl overflow-hidden mb-4 shadow-md">
                          {photoPreview ? (
                            <img src={photoPreview} alt="Photo" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center" style={{ backgroundColor: '#448696' }}>
                              <User size={56} className="text-white" />
                            </div>
                          )}
                          {isUploading && (
                            <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
                              <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin" />
                            </div>
                          )}
                        </div>
                        {isEditing && (
                          <div className="flex gap-2 w-full">
                            <button onClick={() => fileInputRef.current?.click()}
                              className="flex-1 py-2.5 rounded-xl font-semibold text-xs text-white flex items-center justify-center gap-1.5 transition-all hover:opacity-90"
                              style={{ backgroundColor: '#448696' }}>
                              <Upload size={14} /> {photoPreview ? 'Changer' : 'Ajouter'}
                            </button>
                            {photoPreview && (
                              <button onClick={handleDeletePhoto}
                                className="py-2.5 px-3 rounded-xl font-semibold text-xs transition-all hover:opacity-90"
                                style={{ backgroundColor: '#fed774' }}>
                                <Trash2 size={14} />
                              </button>
                            )}
                          </div>
                        )}
                        <input ref={fileInputRef} type="file" className="hidden"
                          accept="image/jpeg,image/png,image/jpg" onChange={handleFileChange} />
                      </div>
                    </div>

                    {/* Infos création */}
                    
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4">Informations système</p>
                      <div className="space-y-3">
                        <Field label="Créé le">
                          <ReadVal>{localEmp.created_at ? new Date(localEmp.created_at).toLocaleString('fr-FR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' }) : '—'}</ReadVal>
                        </Field>
                        <Field label="Créé par">
                          <ReadVal>{localEmp.created_by_name || '—'}</ReadVal>
                        </Field>


                       <Field label="Statut">
                          {isEditing ? (
                            <div className="flex gap-2">
                              {[{ value: 'active', label: 'Actif' }, { value: 'inactive', label: 'Inactif' }].map(({ value, label }) => (
                                <button key={value} type="button"
                                  onClick={() => setLocalEmp(p => ({ ...p, status: value }))}
                                  className="flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all"
                                  style={{
                                    backgroundColor: localEmp.status === value ? (value === 'active' ? '#448696' : '#448696') : 'white',
                                    color: localEmp.status === value ? 'white' : '#6b7280',
                                    borderColor: localEmp.status === value ? (value === 'active' ? '#448696' : '#448696') : '#e5e7eb'
                                  }}>
                                  {label}
                                </button>
                              ))}
                            </div>
                          ) : (
                            <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${localEmp.status === 'active' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                              {localEmp.status === 'active' ? 'Actif' : 'Inactif'}
                            </span>
                          )}
                        </Field>


                      </div>
                    </div>

                  </div>

                  {/* Col 2 — Identité */}
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Identité</p>
                    <Field label="Prénom" required>
                      {isEditing ? <input type="text" name="first_name" value={localEmp.first_name||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                        : <ReadVal>{localEmp.first_name}</ReadVal>}
                    </Field>
                    <Field label="Nom" required>
                      {isEditing ? <input type="text" name="last_name" value={localEmp.last_name||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                        : <ReadVal>{localEmp.last_name}</ReadVal>}
                    </Field>
                    <Field label="Sexe">
                      {isEditing ? (
                        <select name="gender" value={localEmp.gender||'M'} onChange={handleInput} className={inputCls} {...inputEvents}>
                          <option value="M">Masculin</option>
                          <option value="F">Féminin</option>
                        </select>
                      ) : <ReadVal>{localEmp.gender === 'M' ? 'Masculin' : 'Féminin'}</ReadVal>}
                    </Field>
                    <Field label="Date de naissance">
                      {isEditing ? <input type="date" name="date_of_birth" value={localEmp.date_of_birth||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                        : <ReadVal>{fmt(localEmp.date_of_birth)}</ReadVal>}
                    </Field>
                    <Field label="Email" required>
                      {isEditing ? <input type="email" name="email" value={localEmp.email||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                        : <ReadVal><span className="break-all">{localEmp.email}</span></ReadVal>}
                    </Field>
                    <Field label="Téléphone" required>
                      {isEditing ? <input type="tel" name="phone" value={localEmp.phone||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                        : <ReadVal>{localEmp.phone}</ReadVal>}
                    </Field>
                  </div>

                  {/* Col 3 — Adresse + Badge */}
                  <div className="space-y-5">
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Adresse</p>
                      <Field label="Adresse complète">
                        {isEditing ? <textarea name="address" value={localEmp.address||''} onChange={handleInput} rows={3} className={inputCls} {...inputEvents} />
                          : <ReadVal>{localEmp.address || '—'}</ReadVal>}
                      </Field>
                      <Field label="Ville">
                        {isEditing ? <input type="text" name="city" value={localEmp.city||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                          : <ReadVal>{localEmp.city || '—'}</ReadVal>}
                      </Field>
                    </div>

                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Badge</p>
                      <Field label="Numéro de badge">
                        {isEditing ? <input type="text" name="badge_number" value={localEmp.badge_number||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                          : <ReadVal>{localEmp.badge_number || '—'}</ReadVal>}
                      </Field>
                      {rfidEnabled && (
                        <Field label="Badge RFID">
                          {isEditing ? (
                            <div className="relative">
                              <input type="text" value="" onChange={() => {}} onKeyDown={handleRfidKeyDown}
                                onFocus={() => setRfidFieldActive(true)} onBlur={() => setRfidFieldActive(false)}
                                className="absolute inset-0 opacity-0 w-full h-full cursor-pointer z-10" />
                              <div className="w-full px-3 py-2.5 border-2 rounded-lg text-sm cursor-pointer flex items-center justify-between"
                                style={{ borderColor: rfidFieldActive ? '#fed774' : (localEmp.rfid_uid ? '#448696' : '#e5e7eb'), backgroundColor: rfidFieldActive ? '#fffbeb' : 'white', color: localEmp.rfid_uid ? '#1f2937' : '#9ca3af' }}>
                                <div className="flex items-center gap-2">
                                  <Fingerprint size={14} className="text-gray-400" />
                                  <span>{localEmp.rfid_uid || 'Scannez le badge...'}</span>
                                </div>
                                {rfidFieldActive && <span className="text-xs font-semibold text-amber-600">Prêt</span>}
                                {localEmp.rfid_uid && !rfidFieldActive && <span className="text-xs font-semibold text-green-600">✓</span>}
                              </div>
                            </div>
                          ) : (
                            <ReadVal>
                              <span className="flex items-center gap-2"><Fingerprint size={13} className="text-gray-400" /> {localEmp.rfid_uid || '—'}</span>
                            </ReadVal>
                          )}
                          {isEditing && localEmp.rfid_uid && (
                            <button onClick={() => setLocalEmp(p => ({ ...p, rfid_uid: '' }))} className="text-xs font-semibold mt-1" style={{ color: '#448696' }}>
                              Effacer
                            </button>
                          )}
                        </Field>
                      )}
                    </div>
                  </div>
                </div>
              </Tabs.Content>

              {/* ═══════════════════════════════════════════════════
                  ONGLET 2 — POSTE & CONTRAT
              ═══════════════════════════════════════════════════ */}
              <Tabs.Content value="contract">
                <div className="p-6 space-y-5 pb-10">

                  {/* ── ALERTE PÉRIODE D'ESSAI TERMINÉE ── */}
                  {showTrialAlert && !contractAction && (
                    <div className="rounded-2xl border-2 border-orange-300 overflow-hidden"
                      style={{ backgroundColor: '#fff7ed' }}>
                      <div className="px-5 py-4 flex items-center gap-3" style={{ backgroundColor: '#ffedd5' }}>
                        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: '#ea580c' }}>
                          <AlertTriangle size={20} className="text-white" />
                        </div>
                        <div className="flex-1">
                          <p className="font-bold text-orange-900">Période d'essai terminée</p>
                          <p className="text-xs text-orange-700 mt-0.5">
                            La période d'essai s'est terminée le <strong>{fmt(localEmp.trial_period_end_date)}</strong>. Une action est requise.
                          </p>
                        </div>
                      </div>
                      <div className="p-5 grid grid-cols-1 md:grid-cols-3 gap-3">
                        <button onClick={() => { setContractAction('prolongation_essai'); setActionData({ new_trial_end_date: '' }); }}
                          className="p-4 rounded-xl border-2 border-amber-200 bg-white hover:border-amber-400 transition-all text-left group">
                          <div className="w-9 h-9 rounded-xl bg-amber-100 flex items-center justify-center mb-3 group-hover:bg-amber-200 transition-all">
                            <Calendar size={18} className="text-amber-600" />
                          </div>
                          <p className="font-bold text-gray-900 text-sm">Prolonger l'essai</p>
                          <p className="text-xs text-gray-500 mt-1">Définir une nouvelle date de fin de période d'essai</p>
                        </button>
                        <button onClick={() => { setContractAction('confirmation_contrat'); setActionData({ new_contract_start_date: localEmp.contract_start_date || '', new_contract_end_date: localEmp.contract_end_date || '', new_contract_type_id: localEmp.contract_type_id || '' }); }}
                          className="p-4 rounded-xl border-2 border-green-200 bg-white hover:border-green-400 transition-all text-left group">
                          <div className="w-9 h-9 rounded-xl bg-green-100 flex items-center justify-center mb-3 group-hover:bg-green-200 transition-all">
                            <CheckCircle size={18} className="text-green-600" />
                          </div>
                          <p className="font-bold text-gray-900 text-sm">Confirmer le contrat</p>
                          <p className="text-xs text-gray-500 mt-1">Valider et déclencher officiellement le contrat</p>
                        </button>
                        <button onClick={() => { setContractAction('fin_contrat'); setActionData({ notes: '' }); }}
                          className="p-4 rounded-xl border-2 border-red-200 bg-white hover:border-red-400 transition-all text-left group">
                          <div className="w-9 h-9 rounded-xl bg-red-100 flex items-center justify-center mb-3 group-hover:bg-red-200 transition-all">
                            <XCircle size={18} className="text-red-600" />
                          </div>
                          <p className="font-bold text-gray-900 text-sm">Mettre fin</p>
                          <p className="text-xs text-gray-500 mt-1">Essai non concluant - désactiver l'employé</p>
                        </button>
                      </div>
                    </div>
                  )}

                  {/* ── FORMULAIRE ACTION ── */}
                  {contractAction && (
                    <div className="bg-white rounded-2xl border-2 border-gray-200 p-5 space-y-4">
                      <div className="flex items-center justify-between">
                        <p className="font-bold text-gray-900 text-sm">
                          {contractAction === 'prolongation_essai'   && 'Prolonger la période d\'essai'}
                          {contractAction === 'confirmation_contrat'  && 'Confirmer le contrat'}
                          {contractAction === 'fin_contrat'           && 'Mettre fin au contrat'}
                        </p>
                        <button onClick={() => { setContractAction(null); setActionData({}); }}
                          className="text-xs text-gray-400 hover:text-gray-600 font-semibold">Annuler</button>
                      </div>

                      {contractAction === 'prolongation_essai' && (
                        <Field label="Nouvelle date de fin d'essai" required>
                          <input type="date" value={actionData.new_trial_end_date || ''} onChange={e => setActionData(p => ({ ...p, new_trial_end_date: e.target.value }))} className={inputCls} {...inputEvents} />
                        </Field>
                      )}

                      {contractAction === 'confirmation_contrat' && (
                        <>
                          <div className="p-3 rounded-xl text-xs text-amber-700 font-medium" style={{ backgroundColor: '#fffbeb', border: '1px solid #fcd34d' }}>
                            Les dates prévisionnelles ont été pré-remplies. Vérifiez et modifiez si nécessaire avant de confirmer.
                          </div>
                          <Field label="Date de début du contrat" required>
                            <input type="date" value={actionData.new_contract_start_date || ''} onChange={e => setActionData(p => ({ ...p, new_contract_start_date: e.target.value }))} className={inputCls} {...inputEvents} />
                          </Field>
                          {contractTypes.find(ct => ct.id === (actionData.new_contract_type_id || localEmp.contract_type_id))?.has_end_date == 1 && (
                            <Field label="Date de fin du contrat" required>
                              <input type="date" value={actionData.new_contract_end_date || ''} onChange={e => setActionData(p => ({ ...p, new_contract_end_date: e.target.value }))} className={inputCls} {...inputEvents} />
                            </Field>
                          )}
                        </>
                      )}

                      {contractAction === 'fin_contrat' && (
                        <div className="p-4 rounded-xl border border-red-200 bg-red-50 text-sm text-red-800 font-medium">
                          L'employé sera désactivé immédiatement. Cette action sera enregistrée dans l'historique.
                        </div>
                      )}

                      <Field label="Notes (optionnel)">
                        <textarea value={actionData.notes || ''} onChange={e => setActionData(p => ({ ...p, notes: e.target.value }))} rows={2} placeholder="Motif, remarques..." className={inputCls} {...inputEvents} />
                      </Field>

                      <button onClick={handleContractAction} disabled={savingAction}
                        className="w-full py-3 rounded-xl font-bold text-sm text-white transition-all hover:opacity-90 disabled:opacity-50"
                        style={{ backgroundColor: contractAction === 'fin_contrat' ? '#dc2626' : contractAction === 'confirmation_contrat' ? '#16a34a' : '#ea580c' }}>
                        {savingAction ? 'Enregistrement...' : 'Confirmer l\'action'}
                      </button>
                    </div>
                  )}

                  {/* ── GRILLE POSTE + RÉMUNÉRATION ── */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-5">

                    {/* Poste */}
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Poste</p>
                      <Field label="Date d'embauche">
                        {isEditing ? <input type="date" name="hire_date" value={localEmp.hire_date||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                          : <ReadVal>{fmt(localEmp.hire_date)}</ReadVal>}
                      </Field>
                      <Field label="Poste">
                        {isEditing ? <input type="text" name="position" value={localEmp.position||''} onChange={handleInput} className={inputCls} {...inputEvents} placeholder="Technicien..." />
                          : <ReadVal>{localEmp.position || '—'}</ReadVal>}
                      </Field>
                      <Field label="Département">
                        {isEditing ? (
                          <div className="relative" ref={deptDropdownRef}>
                            <button type="button" onClick={() => setDeptDropdownOpen(p => !p)}
                              className="w-full px-3 py-2.5 bg-white border-2 rounded-lg text-sm font-medium flex items-center justify-between transition-all"
                              style={{ borderColor: deptDropdownOpen ? '#448696' : '#e5e7eb', boxShadow: deptDropdownOpen ? '0 0 0 3px rgba(68,134,150,0.1)' : 'none' }}>
                              <span style={{ color: localEmp.department ? '#1f2937' : '#9ca3af' }}>
                                {localEmp.department || 'Sélectionnez un département'}
                              </span>
                              <ChevronDown size={15} className="text-gray-400" style={{ transform: deptDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                            </button>
                            {deptDropdownOpen && (
                              <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-48 overflow-y-auto">
                                <button type="button" onClick={() => { setLocalEmp(p => ({ ...p, department: '' })); setDeptDropdownOpen(false); }}
                                  className="w-full px-3 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-50 border-b border-gray-50 font-medium">
                                  — Aucun département —
                                </button>
                                {departments.map(dept => (
                                  <button type="button" key={dept.id} onClick={() => { setLocalEmp(p => ({ ...p, department: dept.name })); setDeptDropdownOpen(false); }}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0"
                                    style={{ backgroundColor: localEmp.department === dept.name ? '#f0f9fb' : undefined }}>
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: localEmp.department === dept.name ? '#448696' : '#d1d5db' }} />
                                    <span className="text-sm font-medium" style={{ color: localEmp.department === dept.name ? '#448696' : '#374151' }}>{dept.name}</span>
                                    {localEmp.department === dept.name && <Check size={13} className="ml-auto" style={{ color: '#448696' }} />}
                                  </button>
                                ))}
                                {departments.length === 0 && <div className="px-3 py-3 text-sm text-gray-400 text-center">Aucun département</div>}
                              </div>
                            )}
                          </div>
                        ) : <ReadVal>{localEmp.department || '—'}</ReadVal>}
                      </Field>
                    </div>

                 
                   {/* Rémunération */}
                    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
                      <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Rémunération</p>

                      {/* Type de rémunération */}
                      <Field label="Type de rémunération">
                        {isEditing ? (
                          <div className="flex gap-2">
                            {[
                              { value: 'monthly', label: 'Mensuel' },
                              { value: 'hourly',  label: 'Horaire' }
                            ].map(({ value, label }) => (
                              <button key={value} type="button"
                                onClick={() => setLocalEmp(p => ({ ...p, salary_type: value, salary: '', groupe_salarial_id: null, taux_horaire_personnalise: null }))}
                                className="flex-1 py-2.5 rounded-xl text-sm font-semibold border-2 transition-all"
                                style={{
                                  backgroundColor: localEmp.salary_type === value ? '#448696' : 'white',
                                  color: localEmp.salary_type === value ? 'white' : '#6b7280',
                                  borderColor: localEmp.salary_type === value ? '#448696' : '#e5e7eb'
                                }}>
                                {label}
                              </button>
                            ))}
                          </div>
                        ) : <ReadVal>{localEmp.salary_type === 'monthly' ? 'Mensuel (salaire fixe)' : 'Horaire (taux horaire)'}</ReadVal>}
                      </Field>

                      {/* Salaire fixe — si monthly */}
                      {localEmp.salary_type === 'monthly' && (
                        <Field label={`Salaire mensuel${symbole ? ` (${symbole})` : ''}`}>
                          {isEditing
                            ? <input type="number" name="salary" value={localEmp.salary||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                            : <ReadVal colored>{fmtCurrency(localEmp.salary)}</ReadVal>}
                        </Field>
                      )}

                      {/* Groupe salarial — si hourly */}
                      {localEmp.salary_type === 'hourly' && (
                        <Field label="Groupe salarial">
                          {isEditing ? (
                            <div className="relative" ref={groupeDropdownRef}>
                              <button type="button" onClick={() => setGroupeDropdownOpen(p => !p)}
                                className="w-full px-3 py-2.5 bg-white border-2 rounded-lg text-sm font-medium flex items-center justify-between transition-all"
                                style={{ borderColor: groupeDropdownOpen ? '#448696' : '#e5e7eb', boxShadow: groupeDropdownOpen ? '0 0 0 3px rgba(68,134,150,0.1)' : 'none' }}>
                                <span style={{ color: localEmp.groupe_salarial_id ? '#1f2937' : '#9ca3af' }}>
                                  {localEmp.groupe_salarial_id
                                    ? groupesSalariaux.find(g => g.id == localEmp.groupe_salarial_id)?.nom || 'Groupe sélectionné'
                                    : '-- Sélectionner un groupe --'}
                                </span>
                                <ChevronDown size={15} className="text-gray-400" style={{ transform: groupeDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                              </button>
                              {groupeDropdownOpen && (
                                <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-48 overflow-y-auto">
                                  <button type="button"
                                    onClick={() => { setLocalEmp(p => ({ ...p, groupe_salarial_id: null })); setGroupeDropdownOpen(false); }}
                                    className="w-full px-3 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-50 border-b border-gray-50 font-medium">
                                    — Aucun groupe —
                                  </button>
                                  {groupesSalariaux.map(g => (
                                    <button type="button" key={g.id}
                                      onClick={() => { setLocalEmp(p => ({ ...p, groupe_salarial_id: g.id })); setGroupeDropdownOpen(false); }}
                                      className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0"
                                      style={{ backgroundColor: localEmp.groupe_salarial_id == g.id ? '#f0f9fb' : undefined }}>
                                      <div className="w-2 h-2 rounded-full" style={{ backgroundColor: localEmp.groupe_salarial_id == g.id ? '#448696' : '#d1d5db' }} />
                                      <div className="flex-1">
                                        <span className="text-sm font-medium block"
                                          style={{ color: localEmp.groupe_salarial_id == g.id ? '#448696' : '#374151' }}>
                                          {g.nom}
                                        </span>
                                        <span className="text-xs text-gray-400">
                                          {parseFloat(g.taux_horaire).toLocaleString('fr-FR')} {symbole}/h · {g.heures_journalieres}h/jour
                                        </span>
                                      </div>
                                      {localEmp.groupe_salarial_id == g.id && <Check size={13} className="ml-auto" style={{ color: '#448696' }} />}
                                    </button>
                                  ))}
                                </div>
                              )}
                              {localEmp.groupe_salarial_id && groupesSalariaux.find(g => g.id == localEmp.groupe_salarial_id) && (
                                <p className="text-xs text-gray-400 mt-1">
                                  Taux : {parseFloat(groupesSalariaux.find(g => g.id == localEmp.groupe_salarial_id).taux_horaire).toLocaleString('fr-FR')} {symbole}/h
                                  · {groupesSalariaux.find(g => g.id == localEmp.groupe_salarial_id).heures_journalieres}h/jour théoriques
                                </p>
                              )}
                            </div>
                         ) : (
                            <div className="bg-gray-50 px-3 py-2.5 rounded-lg border border-gray-100">
                              <div className="font-medium text-sm text-gray-900">
                                {groupesSalariaux.find(g => g.id == localEmp.groupe_salarial_id)?.nom || '—'}
                              </div>
                              {localEmp.groupe_salarial_id && groupesSalariaux.find(g => g.id == localEmp.groupe_salarial_id) && (
                                <div className="text-xs mt-1" style={{ color: '#448696' }}>
                                  Taux : {parseFloat(groupesSalariaux.find(g => g.id == localEmp.groupe_salarial_id).taux_horaire).toLocaleString('fr-FR')} {symbole}/h
                                  · {groupesSalariaux.find(g => g.id == localEmp.groupe_salarial_id).heures_journalieres}h/jour théoriques
                                </div>
                              )}
                            </div>
                          )}
                        </Field>
                      )}

                      {/* Taux horaire personnalisé — si hourly */}
                      {localEmp.salary_type === 'hourly' && (
                        <Field label={`Taux personnalisé${symbole ? ` (${symbole}/h)` : ''}`}>
                          {isEditing ? (
                            <div className="flex items-center gap-2">
                              <input type="number"
                                value={localEmp.taux_horaire_personnalise || ''}
                                onChange={e => setLocalEmp(p => ({ ...p, taux_horaire_personnalise: e.target.value || null }))}
                                className={inputCls} {...inputEvents}
                                placeholder="Laisser vide = taux du groupe"
                              />
                              <span className="text-xs font-bold text-gray-400 flex-shrink-0">{symbole}/h</span>
                            </div>
                          ) : (
                            <ReadVal colored={!!localEmp.taux_horaire_personnalise}>
                              {localEmp.taux_horaire_personnalise
                                ? `${parseFloat(localEmp.taux_horaire_personnalise).toLocaleString('fr-FR')} ${symbole}/h`
                                : '—'}
                            </ReadVal>
                          )}
                        </Field>
                      )}

                      {/* Mode de paiement */}
                      <Field label="Mode de paiement">
                        {isEditing ? (
                          <div className="flex gap-2 flex-wrap">
                            {[
                              { value: 'especes',      label: 'Espèces' },
                              { value: 'virement',     label: 'Virement' },
                              { value: 'mobile_money', label: 'Mobile Money' }
                            ].map(({ value, label }) => (
                              <button key={value} type="button"
                                onClick={() => setLocalEmp(p => ({ ...p, mode_paiement_salaire: value }))}
                                className="flex-1 py-2.5 rounded-xl text-xs font-semibold border-2 transition-all"
                                style={{
                                  backgroundColor: localEmp.mode_paiement_salaire === value ? '#448696' : 'white',
                                  color: localEmp.mode_paiement_salaire === value ? 'white' : '#6b7280',
                                  borderColor: localEmp.mode_paiement_salaire === value ? '#448696' : '#e5e7eb'
                                }}>
                                {label}
                              </button>
                            ))}
                          </div>
                        ) : (
                          <ReadVal>
                            {localEmp.mode_paiement_salaire === 'especes'      ? 'Espèces' :
                             localEmp.mode_paiement_salaire === 'virement'     ? 'Virement bancaire' :
                             localEmp.mode_paiement_salaire === 'mobile_money' ? 'Mobile Money' : 'Espèces'}
                          </ReadVal>
                        )}
                      </Field>
                    </div>

                    
                    {/* TEST TEMPORAIRE CALCUL HORARIRE - À SUPPRIMER */}
                      {localEmp.salary_type === 'hourly' && (
                        <button
                          type="button"
                          onClick={async () => {
                            const mois = new Date().getMonth() + 1;
                            const annee = new Date().getFullYear();
                            const result = await api.pointages.calculerSalaireHoraire(localEmp.id, mois, annee);
                            console.log('🧮 Résultat calcul salaire:', JSON.stringify(result, null, 2));
                            alert(JSON.stringify(result?.data?.resume || result, null, 2));
                          }}
                          className="w-full py-2 rounded-xl text-xs font-bold text-white"
                          style={{ backgroundColor: '#448696' }}
                        >
                          🧮 Tester calcul salaire horaire
                        </button>
                      )} 


                  </div>

                  {/* ── CONTRAT ── */}
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5 space-y-4">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest">Contrat</p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {/* Type de contrat */}
                      <Field label="Type de contrat">
                        {isEditing ? (
                          <div className="relative" ref={contractTypeRef}>
                            <button type="button" onClick={() => setContractTypeOpen(p => !p)}
                              className="w-full px-3 py-2.5 bg-white border-2 rounded-lg text-sm font-medium flex items-center justify-between transition-all"
                              style={{ borderColor: contractTypeOpen ? '#448696' : '#e5e7eb', boxShadow: contractTypeOpen ? '0 0 0 3px rgba(68,134,150,0.1)' : 'none' }}>
                              <span style={{ color: localEmp.contract_type_id ? '#1f2937' : '#9ca3af' }}>
                                {contractTypes.find(ct => ct.id === localEmp.contract_type_id)?.nom || '— Aucun type —'}
                              </span>
                              <ChevronDown size={15} className="text-gray-400" style={{ transform: contractTypeOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }} />
                            </button>
                            {contractTypeOpen && (
                              <div className="absolute top-full mt-1 left-0 right-0 bg-white rounded-xl shadow-xl border border-gray-100 z-50 max-h-48 overflow-y-auto">
                                <button type="button" onClick={() => { setLocalEmp(p => ({ ...p, contract_type_id: null })); setContractTypeOpen(false); }}
                                  className="w-full px-3 py-2.5 text-left text-sm text-gray-400 hover:bg-gray-50 border-b border-gray-50 font-medium">
                                  — Aucun type —
                                </button>
                                {contractTypes.map(ct => (
                                  <button type="button" key={ct.id} onClick={() => { setLocalEmp(p => ({ ...p, contract_type_id: ct.id })); setContractTypeOpen(false); }}
                                    className="w-full flex items-center gap-2 px-3 py-2.5 text-left hover:bg-gray-50 border-b border-gray-50 last:border-0"
                                    style={{ backgroundColor: localEmp.contract_type_id === ct.id ? '#f0f9fb' : undefined }}>
                                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: localEmp.contract_type_id === ct.id ? '#448696' : '#d1d5db' }} />
                                    <span className="text-sm font-medium" style={{ color: localEmp.contract_type_id === ct.id ? '#448696' : '#374151' }}>{ct.nom}</span>
                                    {localEmp.contract_type_id === ct.id && <Check size={13} className="ml-auto" style={{ color: '#448696' }} />}
                                  </button>
                                ))}
                              </div>
                            )}
                          </div>
                        ) : (
                          <ReadVal>
                            {contractTypes.find(ct => ct.id === localEmp.contract_type_id)?.nom || '—'}
                          </ReadVal>
                        )}
                      </Field>

                      {/* Date début */}
                      {localEmp.contract_type_id && (
                        <Field label={localEmp.trial_period_end_date ? 'Date de début prévisionnelle' : 'Date de début du contrat'}
                          hint={localEmp.trial_period_end_date ? 'Prévisionnelle' : null}>
                          {isEditing ? <input type="date" name="contract_start_date" value={localEmp.contract_start_date||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                            : <ReadVal>{fmt(localEmp.contract_start_date)}</ReadVal>}
                        </Field>
                      )}

                      {/* Date fin — si durée déterminée */}
                      {localEmp.contract_type_id && contractTypes.find(ct => ct.id === localEmp.contract_type_id)?.has_end_date == 1 && (
                        <Field label={localEmp.trial_period_end_date ? 'Date de fin prévisionnelle' : 'Date de fin du contrat'}
                          required hint={localEmp.trial_period_end_date ? 'Prévisionnelle' : null}>
                          {isEditing ? <input type="date" name="contract_end_date" value={localEmp.contract_end_date||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                            : <ReadVal>{fmt(localEmp.contract_end_date)}</ReadVal>}
                        </Field>
                      )}

                      {/* Période d'essai */}
                      {localEmp.contract_type_id && (
                        <Field label="Fin de période d'essai">
                          {isEditing ? <input type="date" name="trial_period_end_date" value={localEmp.trial_period_end_date||''} onChange={handleInput} className={inputCls} {...inputEvents} />
                            : (
                              <div className={`bg-gray-50 px-3 py-2.5 rounded-lg border font-medium text-sm ${isTrialEnded ? 'border-orange-300 text-orange-700' : 'border-gray-100 text-gray-900'}`}
                                style={{ backgroundColor: isTrialEnded ? '#fff7ed' : undefined }}>
                                {localEmp.trial_period_end_date ? (
                                  <span className="flex items-center gap-2">
                                    {fmt(localEmp.trial_period_end_date)}
                                    {isTrialEnded && <span className="text-xs font-bold px-2 py-0.5 rounded-full bg-orange-100 text-orange-700">Terminée</span>}
                                  </span>
                                ) : '—'}
                              </div>
                            )}
                        </Field>
                      )}
                    </div>
                  </div>

                  {/* ── HISTORIQUE ── */}
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                    <p className="text-xs font-bold text-gray-400 uppercase tracking-widest mb-4 flex items-center gap-2">
                      <History size={13} /> Historique contractuel
                    </p>
                    {contractEvents.length === 0 ? (
                      <div className="text-center py-8 text-gray-400 text-sm border-2 border-dashed border-gray-200 rounded-xl">
                        Aucun événement contractuel enregistré
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {contractEvents.map((ev, i) => (
                          <div key={i} className="flex items-start gap-3 p-3 bg-gray-50 rounded-xl border border-gray-100">
                            <div className="w-2 h-2 rounded-full mt-2 flex-shrink-0"
                              style={{ backgroundColor: ev.event_type === 'fin_contrat' ? '#dc2626' : ev.event_type === 'confirmation_contrat' ? '#16a34a' : '#ea580c' }} />
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 flex-wrap">
                                <EventBadge type={ev.event_type} />
                                <span className="text-xs text-gray-400">{ev.event_date ? new Date(ev.event_date).toLocaleDateString('fr-FR') : '—'}</span>
                                <span className="text-xs text-gray-400">par <strong>{ev.created_by_name || '—'}</strong></span>
                              </div>
                              {ev.notes && <p className="text-xs text-gray-600 mt-1.5 italic">"{ev.notes}"</p>}
                              {ev.new_trial_end_date && <p className="text-xs text-gray-500 mt-1">→ Nouvel essai jusqu'au {fmt(ev.new_trial_end_date)}</p>}
                              {ev.new_contract_start_date && <p className="text-xs text-gray-500 mt-1">→ Contrat du {fmt(ev.new_contract_start_date)}{ev.new_contract_end_date ? ` au ${fmt(ev.new_contract_end_date)}` : ''}</p>}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                </div>
              </Tabs.Content>

              {/* ═══════════════════════════════════════════════════
                  ONGLET 3 — DOCUMENTS
              ═══════════════════════════════════════════════════ */}
              <Tabs.Content value="documents">
                <div className="p-6 space-y-4 pb-10">

                  {/* Génération contrat */}
                  {contratsEnabled && (
                  <div className="bg-white rounded-xl border border-gray-100 shadow-sm overflow-hidden">
                    <div className="px-5 py-3.5 flex items-center justify-between"
                      style={{ background: 'linear-gradient(135deg, #448696, #6fa3b0)' }}>
                      <div className="flex items-center gap-2 text-white font-bold text-sm">
                        <FileText size={17} /> Contrat de Travail
                      </div>
                    </div>
                    <div className="p-5 grid md:grid-cols-2 gap-6">
                      {/* Checklist */}
                      <div className="space-y-3">
                        <p className="text-xs font-bold text-gray-500 uppercase">Générer un contrat PDF</p>
                        <div className="bg-gray-50 rounded-xl p-4 border border-gray-200 space-y-2">
                          {[
                            { ok: !!(localEmp.first_name && localEmp.last_name), label: 'Nom et prénom' },
                            { ok: !!localEmp.hire_date, label: "Date d'embauche" },
                            { ok: !!localEmp.position, label: 'Poste', warn: true },
                          ].map(({ ok: isOk, label, warn }) => (
                            <div key={label} className="flex items-center gap-2 text-sm">
                              {isOk ? <CheckCircle size={15} className="text-green-500" /> : warn ? <AlertTriangle size={15} className="text-amber-500" /> : <XCircle size={15} className="text-red-500" />}
                              <span className={isOk ? 'text-green-700 font-medium' : warn ? 'text-amber-700' : 'text-red-700'}>{label}{!isOk && warn ? ' (recommandé)' : ''}</span>
                            </div>
                          ))}
                        </div>
                        <button onClick={generateContract}
                          disabled={isGeneratingContract || hasExistingContract() || !localEmp.first_name || !localEmp.last_name || !localEmp.hire_date}
                          className="w-full py-3 rounded-xl font-bold text-sm text-white flex items-center justify-center gap-2 transition-all hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm"
                          style={{ backgroundColor: hasExistingContract() ? '#9ca3af' : '#448696' }}>
                          {isGeneratingContract ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Génération...</>
                            : hasExistingContract() ? <><FileText size={16} /> Contrat déjà généré</>
                            : <><FileText size={16} /> Générer le contrat</>}
                        </button>
                        {hasExistingContract() && (
                          <p className="text-xs text-blue-600 bg-blue-50 p-3 rounded-xl border border-blue-100">
                            Un contrat existe déjà. Supprimez-le pour en générer un nouveau.
                          </p>
                        )}
                      </div>

                      {/* Contrats existants */}
                      <div className="space-y-3">
                        <p className="text-xs font-bold text-gray-500 uppercase">Contrats existants</p>
                        <div className="bg-gray-50 border border-gray-200 rounded-xl p-4 min-h-[140px]">
                          {documents.filter(d => d.type === 'contrat').length === 0 ? (
                            <div className="flex flex-col items-center justify-center h-full py-6 text-center">
                              <FileText size={28} className="text-gray-300 mb-2" />
                              <p className="text-sm text-gray-400">Aucun contrat généré</p>
                            </div>
                          ) : documents.filter(d => d.type === 'contrat').map((doc, i) => (
                            <div key={i} className="flex items-center justify-between p-3 bg-white rounded-lg border border-gray-200">
                              <div className="flex items-center gap-2">
                                <FileText size={16} className="text-blue-600" />
                                <div>
                                  <p className="text-sm font-semibold text-gray-800">{doc.nom_fichier || `Contrat ${i+1}`}</p>
                                  <p className="text-xs text-gray-400">{doc.date_ajout ? new Date(doc.date_ajout).toLocaleDateString('fr-FR') : '—'}</p>
                                </div>
                              </div>
                              <div className="flex gap-1">
                                <button onClick={() => handleDownload(doc.id, doc.nom_fichier)} className="w-8 h-8 rounded-lg hover:bg-blue-50 flex items-center justify-center">
                                  <Download size={14} className="text-blue-600" />
                                </button>
                                <button onClick={() => handleDeleteDoc(doc.id, doc.type)} className="w-8 h-8 rounded-lg hover:bg-red-50 flex items-center justify-center">
                                  <Trash2 size={14} className="text-red-500" />
                                </button>
                              </div>
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </div>
                   )}

                  {/* Autres documents */}
                  <DocSection title="CV" icon={FileText} type="cv" docs={documents} onUpload={handleFileUpload} onDownload={handleDownload} onDelete={handleDeleteDoc} />
                  <DocSection title="Diplômes & Certifications" icon={BookOpen} type="diplome" docs={documents} onUpload={handleFileUpload} onDownload={handleDownload} onDelete={handleDeleteDoc} multiple />
                  <DocSection title="Pièce d'Identité" icon={File} type="piece_identite" docs={documents} onUpload={handleFileUpload} onDownload={handleDownload} onDelete={handleDeleteDoc} accept="application/pdf,image/jpeg,image/png,image/jpg" />
                  <DocSection title="Autres Documents" icon={File} type="other" docs={documents} onUpload={handleFileUpload} onDownload={handleDownload} onDelete={handleDeleteDoc} multiple />
                </div>
              </Tabs.Content>

            </div>

            {/* ── FOOTER ── */}
            <div className="flex-shrink-0 px-6 py-4 border-t border-gray-100 flex items-center justify-between rounded-b-2xl bg-gray-50">
              <div>
                {activeTab !== 'documents' && (
                  isEditing ? (
                    <div className="flex gap-2">
                      <button onClick={handleCancel}
                        className="px-5 py-2.5 rounded-xl font-semibold text-sm border border-gray-300 text-gray-700 hover:bg-gray-100 transition-all flex items-center gap-2">
                        <X size={15} /> Annuler
                      </button>
                      <button onClick={handleSave}
                        className="px-5 py-2.5 rounded-xl font-semibold text-sm text-white shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                        style={{ backgroundColor: '#448696' }}>
                        <Save size={15} /> Enregistrer
                      </button>
                    </div>
                  ) : (
                    <button onClick={() => setIsEditing(true)}
                      className="px-5 py-2.5 rounded-xl font-semibold text-sm text-white shadow-md hover:shadow-lg transition-all flex items-center gap-2"
                      style={{ backgroundColor: '#448696' }}>
                      <Edit size={15} /> Modifier
                    </button>
                  )
                )}
                {activeTab === 'documents' && (
                  <div className="flex items-center gap-2 text-emerald-600 bg-emerald-50 px-4 py-2 rounded-full text-sm font-medium">
                    <CheckCircle size={16} /> Documents sauvegardés automatiquement
                  </div>
                )}
              </div>
              <button onClick={onClose}
                className="px-5 py-2.5 rounded-xl font-semibold text-sm border border-gray-200 text-gray-700 hover:bg-gray-100 transition-all">
                Fermer
              </button>
            </div>

          </Tabs.Root>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};

export default EmployeeDetailModal;